import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../lib/api';
import { BrightBlueIntegration, IntegrationConfig } from '../../components/integrations/BrightBlueIntegration';
import { OccaIntegration, type OccaIntegrationConfig, type OccaIndexJob } from '../../components/integrations/OccaIntegration';
import { CatalogExportImport } from '../../components/integrations/CatalogExportImport';
import { WebhookEventsList } from '../../components/integrations/WebhookEventsList';
import type { BrightBlueCatalogContext } from '../../components/integrations/types';
import { Button } from '../../components/ui/button';

type ViewMode = 'settings' | 'events';

export default function IntegrationsBrightBlue() {
  const [config, setConfig] = useState<IntegrationConfig | null>(null);
  const [catalogContext, setCatalogContext] = useState<BrightBlueCatalogContext>({
    operators: [],
    defaultOperatorId: null,
    needsOperatorLink: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('settings');
  const [occaConfig, setOccaConfig] = useState<OccaIntegrationConfig | null>(null);
  const [occaIndexJob, setOccaIndexJob] = useState<OccaIndexJob | null>(null);

  const fetchCatalogContext = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/settings/integrations/brightblue/catalog-context');
      if (res.ok) {
        const data = (await res.json()) as BrightBlueCatalogContext;
        setCatalogContext(data);
      }
    } catch {
      // Non-fatal — manual operator entry still works
    }
  }, []);

  const fetchOccaConfig = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/settings/integrations/occa');
      if (res.status === 404) {
        setOccaConfig(null);
      } else if (res.ok) {
        setOccaConfig((await res.json()) as OccaIntegrationConfig);
      }
    } catch {
      // Non-fatal
    }
  }, []);

  const fetchOccaIndexJob = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/settings/integrations/occa/sync-index/jobs/current');
      if (res.status === 404) {
        setOccaIndexJob(null);
      } else if (res.ok) {
        setOccaIndexJob((await res.json()) as OccaIndexJob);
      }
    } catch {
      // Non-fatal
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/settings/integrations/brightblue');
      if (res.status === 404) {
        setConfig(null);
      } else if (res.ok) {
        const data = await res.json();
        setConfig(data);
      } else if (res.status === 403) {
        setError('You do not have permission to view integration settings.');
      } else {
        setError('Failed to load integration settings.');
      }
    } catch {
      setError('Failed to connect to the server.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void Promise.all([fetchConfig(), fetchCatalogContext(), fetchOccaConfig(), fetchOccaIndexJob()]);
  }, [fetchConfig, fetchCatalogContext, fetchOccaConfig, fetchOccaIndexJob]);

  useEffect(() => {
    if (!occaIndexJob || (occaIndexJob.status !== 'queued' && occaIndexJob.status !== 'running')) {
      return;
    }
    const timer = window.setInterval(() => {
      void fetchOccaIndexJob();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [occaIndexJob, fetchOccaIndexJob]);

  useEffect(() => {
    if (occaIndexJob?.status === 'completed') {
      void fetchOccaConfig();
    }
  }, [occaIndexJob?.status, fetchOccaConfig]);

  const handleEnable = async (tenantId: string): Promise<{ webhookSecret?: string }> => {
    const res = await apiFetch('/api/v1/settings/integrations/brightblue', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        providerTenantId: tenantId,
        syncProducts: true,
        syncMachines: true,
        syncTransactions: true,
        autoCreateProducts: false,
      }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to enable integration');
    }

    const data = await res.json();
    await fetchConfig();
    return { webhookSecret: data.webhookSecret };
  };

  const handleUpdate = async (updates: Partial<IntegrationConfig>): Promise<void> => {
    const res = await apiFetch('/api/v1/settings/integrations/brightblue', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to update integration');
    }

    await fetchConfig();
  };

  const handleDisable = async (): Promise<void> => {
    const res = await apiFetch('/api/v1/settings/integrations/brightblue', {
      method: 'DELETE',
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to disable integration');
    }

    setConfig(null);
  };

  const handleRegenerateSecret = async (): Promise<{ webhookSecret: string }> => {
    const res = await apiFetch('/api/v1/settings/integrations/brightblue/regenerate-secret', {
      method: 'POST',
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to regenerate secret');
    }

    const data = await res.json();
    return { webhookSecret: data.webhookSecret };
  };

  const handleViewAllEvents = () => {
    setViewMode('events');
  };

  const handleOccaEnable = async (input: {
    clientId: string;
    secret: string;
    baseUrl: string;
  }): Promise<void> => {
    const res = await apiFetch('/api/v1/settings/integrations/occa', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to connect Occa');
    }
    await fetchOccaConfig();
  };

  const handleOccaDisable = async (): Promise<void> => {
    const res = await apiFetch('/api/v1/settings/integrations/occa', { method: 'DELETE' });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to disable Occa');
    }
    setOccaConfig(null);
    setOccaIndexJob(null);
  };

  const handleOccaSyncIndex = async (): Promise<OccaIndexJob> => {
    const res = await apiFetch('/api/v1/settings/integrations/occa/sync-index', { method: 'POST' });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to start Occa index sync');
    }
    const job = (await res.json()) as OccaIndexJob;
    setOccaIndexJob(job);
    return job;
  };

  return (
    <div className="min-h-screen">
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold">Integrations</h1>
            <p className="text-muted-foreground">Manage external platform integrations</p>
          </div>
          {viewMode === 'events' && (
            <Button variant="outline" onClick={() => setViewMode('settings')}>
              Back to Settings
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading...</div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-destructive mb-4">{error}</p>
            <Button onClick={fetchConfig}>Retry</Button>
          </div>
        ) : viewMode === 'events' ? (
          <WebhookEventsList />
        ) : (
          <div className="space-y-6">
            <CatalogExportImport onCatalogContextUpdated={fetchCatalogContext} />
            <BrightBlueIntegration
              config={config}
              catalogOperators={catalogContext.operators}
              defaultOperatorId={catalogContext.defaultOperatorId}
              needsOperatorLink={catalogContext.needsOperatorLink}
              onEnable={handleEnable}
              onUpdate={handleUpdate}
              onDisable={handleDisable}
              onRegenerateSecret={handleRegenerateSecret}
              onViewAllEvents={handleViewAllEvents}
            />
            <OccaIntegration
              config={occaConfig}
              indexJob={occaIndexJob}
              onEnable={handleOccaEnable}
              onDisable={handleOccaDisable}
              onSyncIndex={handleOccaSyncIndex}
              onRefreshJob={fetchOccaIndexJob}
            />
          </div>
        )}
      </div>
    </div>
  );
}
